import { ErrorBox, PageSection, Spinner } from "@client/components/ui";
import { AttentionCard } from "@client/features/pulpit/AttentionCard";
import { Indicators } from "@client/features/pulpit/Indicators";
import { indicatorValuesFromOverview } from "@client/features/pulpit/lib";
import { PortfolioCard } from "@client/features/pulpit/PortfolioCard";
import { RegimeBar } from "@client/features/pulpit/RegimeBar";
import { RotationCard } from "@client/features/pulpit/RotationCard";
import { basketsQuery, overviewQuery, settingsQuery, tokensQuery } from "@client/lib/queries";
import { attentionList } from "@shared/basket";
import { evaluateCondition } from "@shared/conditions";
import { portfolioExposure } from "@shared/portfolio";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

export const Route = createFileRoute("/")({
  component: PulpitPage,
});

function PulpitPage() {
  const overviewQ = useQuery(overviewQuery());
  const basketsQ = useQuery(basketsQuery());
  const tokensQ = useQuery(tokensQuery());
  const settingsQ = useQuery(settingsQuery());
  const exposure = useMemo(
    () => portfolioExposure(basketsQ.data ?? [], tokensQ.data ?? []),
    [basketsQ.data, tokensQ.data],
  );

  const error = overviewQ.error ?? basketsQ.error ?? tokensQ.error ?? settingsQ.error;
  if (error) {
    return (
      <PageSection>
        <ErrorBox error={error} />
      </PageSection>
    );
  }

  const overview = overviewQ.data;
  const baskets = basketsQ.data;
  const tokens = tokensQ.data;
  const settings = settingsQ.data;
  if (!overview || !baskets || !tokens || !settings) {
    return (
      <PageSection>
        <div className="flex justify-center py-20">
          <Spinner size={28} />
        </div>
      </PageSection>
    );
  }

  const tokensById = new Map(tokens.map((t) => [t.id, t]));
  const values = indicatorValuesFromOverview(overview.indicators);
  const items = attentionList(baskets, tokensById, settings, (b) =>
    b.startIndicator ? evaluateCondition(b.startIndicator, values) : null,
  );

  return (
    <PageSection>
      <RegimeBar regime={overview.regime} />
      <Indicators indicators={overview.indicators} layout={settings.dashboardLayout} />
      <RotationCard />
      <PortfolioCard exposure={exposure} fx={overview.fx} plnBeside={settings.plnBeside} />
      <AttentionCard items={items} />
    </PageSection>
  );
}
